import { memo, useCallback, useContext, useRef } from "react";
import PropTypes from "prop-types";
import { MonthView } from "react-calendar";
import { isAfter, isBefore, isDate, isSameDay } from "date-fns";
import "./CalendarMonth.scss";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import CalendarMonthHeadline from "app/pages/.shared/CalendarList/CalendarMonthHeadline";
import { FlightOutboundFilledIcon } from "app/icons/travel";
import { ICON_SIZES } from "app/constants";
import useFixCalendarRtlDayOffset from "app/utils/hooks/useFixCalendarRtlDayOffset";
import { formatCalendarDay } from "app/utils/utils";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";

const CalendarMonth = ({
	month,
	selectedDate = [],
	handleDateClick,
	departureDateMax,
	departureDateMin,
	checkDateAvailability = () => {},
	displayCalendarIcon = true,
}) => {
	const monthRef = useRef(null);
	useFixCalendarRtlDayOffset(monthRef, month);
	const { resolution } = useContext(AppGlobalsContext);
	const isTablet = resolution === RESOLUTION.MEDIUM;

	const monthValue = Number(month.split("/")[0]);
	const yearValue = Number(month.split("/")[1]);

	const activeStartDate = new Date(yearValue, monthValue, 1);
	// Monday-first offset, matching react-calendar's own getDayOfWeek formula.
	const dayOffset = (activeStartDate.getDay() + 6) % 7;

	let departureDateMaxUTC = new Date(departureDateMax);
	departureDateMaxUTC = new Date(
		departureDateMaxUTC.getFullYear(),
		departureDateMaxUTC.getMonth(),
		departureDateMaxUTC.getDate(),
		0,
		0,
		0
	);

	let departureDateMinUTC = new Date(departureDateMin);
	departureDateMinUTC = new Date(
		departureDateMinUTC.getFullYear(),
		departureDateMinUTC.getMonth(),
		departureDateMinUTC.getDate(),
		0,
		0,
		0
	);

	const renderTileContent = useCallback(
		({ date }) => {
			if (selectedDate?.length > 0) {
				const isDepartureDate = isSameDay(date, new Date(selectedDate[0]));
				const isEndDate = isSameDay(date, new Date(selectedDate[1]));

				if (isDepartureDate || isEndDate) {
					return <FlightOutboundFilledIcon size={ICON_SIZES.XS} />;
				}
			}

			return null;
		},
		[selectedDate]
	);

	const getTileClassName = useCallback(
		({ date, view }) => {
			if (view === "month") {
				if (
					checkDateAvailability(date) ||
					isBefore(date, departureDateMinUTC) ||
					isAfter(date, departureDateMaxUTC) ||
					(selectedDate && selectedDate[0] && !isDate(selectedDate[0])) || // should not disable depart date
					(selectedDate && selectedDate[1] && !isDate(selectedDate[1])) || // should not disable end date
					(selectedDate[0] &&
						selectedDate[1] &&
						isBefore(date, selectedDate[0]) &&
						isAfter(date, selectedDate[1])) // should not disable dates between depart and end date
				) {
					return "date-picker-calendar-tile__tile date-picker-calendar-tile__tile--disabled";
				}
			}
			return "date-picker-calendar-tile__tile";
		},
		[departureDateMinUTC, departureDateMaxUTC, selectedDate]
	);

	return (
		<div
			className="datepicker-calendar-month"
			ref={monthRef}
			style={{ "--day-offset": dayOffset }}
		>
			<div>
				<Typography
					variant={isTablet ? TYPOGRAPHY_VARIANTS.LARGE : TYPOGRAPHY_VARIANTS.REGULAR}
					isBold
					component="p"
					className="datepicker-calendar-month__month"
				>
					<CalendarMonthHeadline monthValue={monthValue} yearValue={yearValue} />
				</Typography>
			</div>
			<MonthView
				value={selectedDate}
				activeStartDate={activeStartDate}
				tileClassName={getTileClassName}
				formatDay={formatCalendarDay}
				onClick={handleDateClick}
				tileDisabled={({ date }) => {
					return checkDateAvailability(date);
				}}
				{...(displayCalendarIcon && { tileContent: renderTileContent })}
			/>
		</div>
	);
};

CalendarMonth.propTypes = {
	month: PropTypes.string,
	selectedDate: PropTypes.array,
	handleDateClick: PropTypes.func,
	departureDateMin: PropTypes.number,
	departureDateMax: PropTypes.number,
	checkDateAvailability: PropTypes.func,
	displayCalendarIcon: PropTypes.bool,
};

export default memo(CalendarMonth);
